import { sendEmailVerification, signInWithEmailAndPassword, type User } from 'firebase/auth';
import { arrayUnion, doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { auth, db } from '@/lib/firebase';
import { apiClient } from '@/services/api/client';

export const EMAIL_ALREADY_IN_USE_ROLE_MESSAGE =
  "Email dÃ©jÃ  utilisÃ© avec un mot de passe diffÃ©rent â€” connectez-vous d'abord";

const PRODUCTION_APP_URL = 'https://acquizgroup-plateform.web.app';

type RegistrationRole = 'buyer' | 'seller' | 'broker' | 'consultant' | 'partner';

interface AddRoleOptions {
  email: string;
  role: RegistrationRole;
  password?: string;
  profileUpdates?: Record<string, unknown>;
}

const normalizeEmail = (email?: string | null) => (email || '').trim().toLowerCase();

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (!value || typeof value !== 'object') return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
};

const stripUndefined = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(stripUndefined).filter((item) => item !== undefined);
  }

  if (isPlainObject(value)) {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([, entryValue]) => entryValue !== undefined)
        .map(([key, entryValue]) => [key, stripUndefined(entryValue)])
    );
  }

  return value;
};

export const isEmailAlreadyInUseError = (error: unknown) =>
  (error as { code?: string } | null)?.code === 'auth/email-already-in-use';

export const getErrorMessage = (error: unknown, fallback: string) =>
  error instanceof Error ? error.message : fallback;

  export async function assignRoleViaNestJS(user: User, role: RegistrationRole) {
  try {
    const idToken = await user.getIdToken(true);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 8000); // 8 sec timeout
    try {
      await apiClient.post(
        '/auth/assign-role',
        { uid: user.uid, role },
        { 
          headers: { Authorization: `Bearer ${idToken}` },
          signal: controller.signal,
        }
      );
    } finally {
      clearTimeout(timeoutId);
    }
    try {
      await user.getIdToken(true);
    } catch (refreshError) {
      console.warn('Token refresh after role assignment failed:', refreshError);
    }
  } catch (error: any) {
    if (error?.name === 'AbortError' || error?.code === 'ERR_CANCELED') {
      console.warn(`${role} role assignment timed out — continuing anyway`);
      return; // Ne pas bloquer le flow
    }
    console.error(`${role} role assignment failed:`, error);
    throw error;
  }
}

export async function sendVerificationEmailBestEffort(user: User) {
  if (user.emailVerified) return;
  try {
    await sendEmailVerification(user, {
      url: `${PRODUCTION_APP_URL}/login`,
      handleCodeInApp: false,
    });
    console.log('Firebase verification email sent to:', user.email);
  } catch (error) {
    console.warn('Firebase verification email deferred:', error);
  }
}

export async function addRoleToCurrentUser({ email, role, password, profileUpdates = {} }: AddRoleOptions) {
  let currentUser = auth.currentUser;

  if (!currentUser || normalizeEmail(currentUser.email) !== normalizeEmail(email)) {
    if (!password) {
      throw new Error(EMAIL_ALREADY_IN_USE_ROLE_MESSAGE);
    }
    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      currentUser = cred.user;
    } catch {
      throw new Error(EMAIL_ALREADY_IN_USE_ROLE_MESSAGE);
    }
  }

  const { roles: _roles, createdAt: _createdAt, ...safeProfileUpdates } = profileUpdates;

  await setDoc(
    doc(db, 'users', currentUser.uid),
    stripUndefined({
      ...safeProfileUpdates,
      email: currentUser.email || email,
      roles: arrayUnion(role),
      updatedAt: serverTimestamp(),
    }) as Record<string, unknown>,
    { merge: true }
  );

  await assignRoleViaNestJS(currentUser, role);
  // Note: Firebase native verification email intentionally NOT sent here.
  // Resend handles the welcome/confirmation email via sendWelcomeEmail callable.

  return currentUser;
}
